import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useResetRecoilState } from 'recoil';
import { useCookies } from 'react-cookie';
import { useSetAtom } from 'jotai';

import { useRefreshSession } from '@/states/common';
import { ConnectionQuery } from '@/queries/connection';
import { useChangeBusiness } from '@/hooks/api/business';
import { useSetPermissions } from '@/hooks/api/user';
import { ChartsState, SelectedInsightsState, useInsights } from '@/states';
import { ReportsContainer, ClientContainer, UserContainer } from '@/providers';
import { COOKIE_KEYS } from '@/constants';
import { businessSwitchAtom } from '@/states/business-switch';

export interface SwitchBusinessParams {
  business: any;
  currentPath: string;
  currentClientId?: string;
}

/**
 * Orchestrates business switching by properly invalidating all state layers
 * without requiring a page reload.
 *
 * This hook coordinates:
 * - UserContainer and ClientContainer state updates
 * - React Query cache invalidation
 * - Recoil state resets
 * - Session refresh
 * - URL navigation
 */
export const useSwitchBusiness = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const refreshSession = useRefreshSession();
  const changeBusiness = useChangeBusiness();
  const setCurrentBusinessPermission = useSetPermissions();
  const invalidateDataSources = ConnectionQuery.useInvalidateDataSources();
  const invalidateDestinations = ConnectionQuery.useInvalidateDestinations();
  const { clearInsights } = useInsights();
  const resetChart = useResetRecoilState(ChartsState);
  const resetSelectedInsights = useResetRecoilState(SelectedInsightsState);
  const { resetReportsState } = ReportsContainer.useContainer();
  const {
    updateSelectedClient,
    updateClients,
  } = ClientContainer.useContainer();
  const { setBusiness, setBusinessOptions } = UserContainer.useContainer();
  const [cookies] = useCookies([COOKIE_KEYS.ACCESS_TOKEN]);
  const setBusinessSwitch = useSetAtom(businessSwitchAtom);

  const switchBusiness = useCallback(
    async ({
      business,
      currentPath,
      currentClientId,
    }: SwitchBusinessParams) => {
      try {
        // Set global loading state at the start
        setBusinessSwitch({
          isSwitching: true,
          message: 'Switching business...',
        });

        // Step 1: Clear all Recoil state tied to old business/client FIRST
        // This prevents stale data from being displayed during the transition
        clearInsights();
        resetChart();
        resetSelectedInsights();
        resetReportsState();

        // Step 2: Update business in containers and localStorage
        // changeBusiness internally calls getClients() to fetch fresh client data
        await changeBusiness(business);

        // Step 3: Sync permissions for new business
        setCurrentBusinessPermission(business.id);

        // Step 4: Update the selected client in ClientContainer
        // Note: changeBusiness already sets the first client in localStorage,
        // but we need to explicitly update the ClientContainer state as well
        const newClientId = business.clients?.[0]?.id;
        if (newClientId) {
          updateSelectedClient(newClientId);
        }

        // Step 5: Navigate to new workspace URL FIRST
        // This updates the URL which the session query reads from
        if (newClientId) {
          const newUrl = currentPath.replace(
            `/ws/${currentClientId}`,
            `/ws/${newClientId}`
          );
          navigate(newUrl);
        }

        // Step 6: Invalidate ALL React Query caches aggressively
        // Clear everything to force fresh data
        await queryClient.invalidateQueries();

        // Also invalidate specific keys for good measure
        await invalidateDataSources(business.id);
        await invalidateDestinations();

        // Step 7: Force session refetch
        // This will fetch fresh data for the new business/client from the server
        // The session query reads clientId from the URL (which we just updated)
        await refreshSession({ refetch: true });

        // Step 8: Update UserContainer and ClientContainer with fresh session data
        // This ensures all components get the latest business/client data
        const { accessToken } = cookies;
        const sessionQueryKey = ['session', accessToken];
        const freshSessionData: any = queryClient.getQueryData(sessionQueryKey);

        if (freshSessionData) {
          // Update business data in UserContainer
          if (freshSessionData.business) {
            setBusiness(freshSessionData.business);
          }
          if (freshSessionData.businessOptions) {
            setBusinessOptions(freshSessionData.businessOptions);
          }

          // CRITICAL: Update the clients array in ClientContainer
          // This is what WorkspaceSwitcher reads to display available workspaces
          if (freshSessionData.clients) {
            updateClients(freshSessionData.clients);
          }

          // Update client data in ClientContainer from session
          // This ensures the selected client matches what the session knows
          if (freshSessionData.client && freshSessionData.client.id) {
            updateSelectedClient(freshSessionData.client.id);
          }
        }

        // Reset global loading state after successful switch
        setBusinessSwitch({ isSwitching: false });
      } catch (error) {
        // Reset loading state on error
        setBusinessSwitch({ isSwitching: false });
        // Re-throw the error so calling components can handle it
        throw error;
      }
    },
    [
      changeBusiness,
      setCurrentBusinessPermission,
      clearInsights,
      resetChart,
      resetSelectedInsights,
      resetReportsState,
      queryClient,
      invalidateDataSources,
      invalidateDestinations,
      refreshSession,
      navigate,
      updateSelectedClient,
      updateClients,
      setBusiness,
      setBusinessOptions,
      cookies.accessToken,
      setBusinessSwitch,
    ]
  );

  return switchBusiness;
};
